/**
 * Shows integration health warning banners to Organization administrators on the integrations
 * settings page.
 *
 * 1. Query the health state for each installed integration.
 * 2. Render each unhealthy integration banner with a retry action.
 * 3. Keep the feature flag, Employee id, and permission gates at the app boundary.
 *
 * The banner renders only on the integrations settings page for admins. Continues in the
 * individual integration health banner components.
 */
import React, { FC } from 'react';
import { useLocation } from 'react-router-dom';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { useFlag } from '@modernloop/shared/feature-flag';

import { Permission, useIntegrationHealthCheckBannersQuery } from 'src/generated/mloop-graphql';

import { useHasUserPermission } from 'src/hooks/api/user/get';
import useEmployeeId from 'src/hooks/useEmployeeId';

import { AppWideBannerAction } from '../AppWideBanner';

import AshbyHealthCheckBanner from './AshbyHealthCheckBanner';
import GemHealthCheckBanner from './GemHealthCheckBanner';
import GoogleHealthCheckBanner from './GoogleHealthCheckBanner';
import GreenhouseHealthCheckBanner from './GreenhouseHealthCheckBanner';
import LeverHealthCheckBanner from './LeverHealthCheckBanner';
import MicrosoftGccHighHealthCheckBanner from './MicrosoftGccHighHealthCheckBanner';
import MicrosoftHealthCheckBanner from './MicrosoftHealthCheckBanner';
import SmartRecruitersHealthCheckBanner from './SmartRecruitersHealthCheckBanner';
import ZoomHealthCheckBanner from './ZoomHealthCheckBanner';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IntegrationCheckHealth = gql`
  ${GoogleHealthCheckBanner.fragments.google}
  ${MicrosoftHealthCheckBanner.fragments.microsoft}
  ${MicrosoftGccHighHealthCheckBanner.fragments.microsoftGccHigh}
  ${GreenhouseHealthCheckBanner.fragments.greenhouse}
  ${LeverHealthCheckBanner.fragments.lever}
  ${AshbyHealthCheckBanner.fragments.ashby}
  ${ZoomHealthCheckBanner.fragments.zoom}
  ${GemHealthCheckBanner.fragments.gem}
  ${SmartRecruitersHealthCheckBanner.fragments.smartRecruiters}
  query IntegrationHealthCheckBanners($force: Boolean!) {
    integrationCheckHealth(input: { force: $force }) {
      id
      google {
        ...GoogleHealthCheckBanner_google
      }
      microsoft {
        ...MicrosoftHealthCheckBanner_microsoft
      }
      microsoftGccHigh {
        ...MicrosoftHealthCheckBanner_microsoftGccHigh
      }
      zoom {
        ...ZoomHealthCheckBanner_zoom
      }
      greenhouseV3 {
        ...GreenhouseHealthCheckBanner_greenhouse
      }
      ashby {
        ...AshbyHealthCheckBanner_ashby
      }
      lever {
        ...LeverHealthCheckBanner_lever
      }
      gem {
        ...GemHealthCheckBanner_gem
      }
      smartRecruiters {
        ...SmartRecruitersHealthCheckBanner_smartRecruiters
      }
    }
  }
`;

const IntegrationHealthCheckBannersInner: FC = () => {
  const { t } = useTranslation();
  const [isRefetching, setIsRefetching] = React.useState(false);

  const { data, loading, error, refetch } = useIntegrationHealthCheckBannersQuery({
    variables: {
      force: false,
    },
    context: {
      batch: false,
    },
  });

  const retry: AppWideBannerAction = {
    label: isRefetching ? t('hiring-dashboard-nav:nav.banner.trying') : t('hiring-dashboard-nav:nav.banner.tryAgain'),
    onClick: async () => {
      setIsRefetching(true);
      await refetch({ force: true });
      setIsRefetching(false);
    },
  };

  if (loading) {
    return null;
  }

  if (error) {
    console.error(error);
    return null;
  }

  const google = data?.integrationCheckHealth?.google;
  const microsoft = data?.integrationCheckHealth?.microsoft;
  const microsoftGccHigh = data?.integrationCheckHealth?.microsoftGccHigh;
  const greenhouse = data?.integrationCheckHealth?.greenhouseV3;
  const lever = data?.integrationCheckHealth?.lever;
  const ashby = data?.integrationCheckHealth?.ashby;
  const zoom = data?.integrationCheckHealth?.zoom;
  const gem = data?.integrationCheckHealth?.gem;
  const smartRecruiters = data?.integrationCheckHealth?.smartRecruiters;

  return (
    <>
      {google && <GoogleHealthCheckBanner google={google} retry={retry} />}
      {microsoft && <MicrosoftHealthCheckBanner microsoft={microsoft} retry={retry} />}
      {microsoftGccHigh && <MicrosoftGccHighHealthCheckBanner microsoftGccHigh={microsoftGccHigh} retry={retry} />}
      {greenhouse && <GreenhouseHealthCheckBanner greenhouse={greenhouse} retry={retry} />}
      {lever && <LeverHealthCheckBanner lever={lever} retry={retry} />}
      {ashby && <AshbyHealthCheckBanner ashby={ashby} retry={retry} />}
      {zoom && <ZoomHealthCheckBanner zoom={zoom} retry={retry} />}
      {gem && <GemHealthCheckBanner gem={gem} retry={retry} />}
      {smartRecruiters && <SmartRecruitersHealthCheckBanner smartRecruiters={smartRecruiters} retry={retry} />}
    </>
  );
};

const IntegrationHealthCheckBanners: FC = () => {
  const showHealthCheckBanner = useFlag('health_check_banner');
  const empId = useEmployeeId();
  const canManageOrgSettings = useHasUserPermission(Permission.CanManageOrgSettings);
  const { pathname } = useLocation();

  if (!pathname.includes('/settings/organization/integrations')) {
    return null;
  }

  if (!showHealthCheckBanner || !empId) {
    // dont show if feature flag is off or if we dont have an employee id
    return null;
  }

  // Only show to users with admin (CanManageOrgSettings) permission; hide while loading
  if (!canManageOrgSettings) {
    return null;
  }

  return <IntegrationHealthCheckBannersInner />;
};

export default IntegrationHealthCheckBanners;
